import gql from "graphql-tag"
import { basicAreaFragment } from "./basicAreaFragment"
import { mediumProblemFragment } from "./mediumProblemFragment"
import { mediumRouteFragment } from "./mediumRouteFragment"
import { basicMediaFragment } from "../../media/graphql/basicMediaFragment"
import { basicGuideFragment } from "./basicGuideFragment"
import { fullAreaFragment } from "./areas"
import { fullProblemFragment } from "./problems"
import { fullRouteFragment } from "./routes"
import { basicSendFragment } from "../../sends/graphql/basicSendFragment"
import { basicProblemFragment } from "./basicProblemFragment"
import { basicRouteFragment } from "./basicRouteFragment"

export const fullGuideFragment = gql`
  fragment fullGuideFragment on Guide {
    ...basicGuideFragment
    version
    description
    directions
    access
    history
    camping
    country
    region
    woeid
    firstEditorId
    hasMedia
    advocates {
      _id
      name
      link
      picture
    }
    minRouteGrade
    maxRouteGrade
    minProblemGrade
    maxProblemGrade
    areas {
      ...basicAreaFragment
    }
  }
  ${basicAreaFragment}
  ${basicGuideFragment}
`

export const guideDetailsFragment = gql`
  fragment guideDetailsFragment on Guide {
    _id
    slug
    name
    type
    hasMedia
    areas {
      ...basicAreaFragment
    }
    routes {
      ...mediumRouteFragment
    }
    problems {
      ...mediumProblemFragment
    }
    media {
      ...basicMediaFragment
    }
    sends {
      ...basicSendFragment
    }
    leaderboardScores {
      problems {
        totalSends
        totalPoints
        top10TotalPoints
      }
      routes {
        totalSends
        totalPoints
        top10TotalPoints
      }
    }
  }
  ${mediumProblemFragment}
  ${mediumRouteFragment}
  ${basicMediaFragment}
  ${basicSendFragment}
  ${basicAreaFragment}
`

export const GET_GUIDES = gql`
  query GetGuides {
    guides {
      ...basicGuideFragment
    }
  }
  ${basicGuideFragment}
`

export const GuideQuery = gql`
  query GuideQuery($slug: String!, $id: String) {
    guide(slug: $slug, id: $id) {
      ...fullGuideFragment
    }
  }
  ${fullGuideFragment}
`

export const GET_GUIDE_DETAILS = gql`
  query GetGuideDetails($slug: String!) {
    guide(slug: $slug) {
      ...guideDetailsFragment
    }
  }
  ${guideDetailsFragment}
`

export const GET_GUIDE_GRADE_DISTRIBUTION = gql`
  query GetGuideGradeDistribution($slug: String!) {
    guide(slug: $slug) {
      _id
      type
      problems {
        _id
        grade
      }
      routes {
        _id
        grade
      }
    }
  }
`

export const GET_GUIDE_ROUTE_GRADES = gql`
  query GetGuideRouteGrades($slug: String!) {
    guide(slug: $slug) {
      _id
      type
      routes {
        _id
        grade
      }
    }
  }
`

export const GET_GUIDE_PROBLEM_GRADES = gql`
  query GetGuideProblemGrades($slug: String!) {
    guide(slug: $slug) {
      _id
      type
      problems {
        _id
        grade
      }
    }
  }
`

export const getGuidesClosestToLocationQuery = gql`
  query getGuidesClosestToLocation($point: PointInput!) {
    getGuidesClosestToLocation(point: $point) {
      ...basicGuideFragment
    }
  }
  ${basicGuideFragment}
`

export const EDIT_GUIDE = gql`
  mutation EditGuide($guide: GuideInput!) {
    editGuide(guide: $guide) {
      ...fullGuideFragment
    }
  }
  ${fullGuideFragment}
`
export const CREATE_GUIDE = gql`
  mutation CreateGuide($guide: GuideInput!) {
    createGuide(guide: $guide) {
      ...fullGuideFragment
    }
  }
  ${fullGuideFragment}
`

export const DELETE_GUIDE = gql`
  mutation DeleteGuide($guideId: String!) {
    deleteGuide(guideId: $guideId) {
      ...fullGuideFragment
    }
  }
  ${fullGuideFragment}
`

export const addProblemToGuideMutate = gql`
  mutation addProblemToGuideMutate($problemId: String!, $guideId: String!) {
    addProblemToGuide(problemId: $problemId, guideId: $guideId) {
      ...fullProblemFragment
    }
  }
  ${fullProblemFragment}
`

export const addRouteToGuideMutate = gql`
  mutation addRouteToGuideMutate($routeId: String!, $guideId: String!) {
    addRouteToGuide(routeId: $routeId, guideId: $guideId) {
      ...fullRouteFragment
    }
  }
  ${fullRouteFragment}
`

export const addAreaToGuideMutate = gql`
  mutation addAreaToGuideMutate($areaId: String!, $guideId: String!) {
    addAreaToGuide(areaId: $areaId, guideId: $guideId) {
      ...fullAreaFragment
      parentArea {
        ...basicAreaFragment
        parentArea {
          ...basicAreaFragment
        }
      }
      guide {
        ...basicGuideFragment
      }
    }
  }
  ${fullAreaFragment}
  ${basicAreaFragment}
  ${basicGuideFragment}
`

export const removeProblemFromGuideMutate = gql`
  mutation removeProblemFromGuideMutate(
    $problemId: String!
    $guideId: String!
  ) {
    removeProblemFromGuide(problemId: $problemId, guideId: $guideId) {
      ...fullProblemFragment
    }
  }
  ${fullProblemFragment}
`

export const removeRouteFromGuideMutate = gql`
  mutation removeRouteFromGuideMutate($routeId: String!, $guideId: String!) {
    removeRouteFromGuide(routeId: $routeId, guideId: $guideId) {
      ...fullRouteFragment
    }
  }
  ${fullRouteFragment}
`

export const removeAreaFromGuideMutate = gql`
  mutation removeAreaFromGuideMutate($areaId: String!, $guideId: String!) {
    removeAreaFromGuide(areaId: $areaId, guideId: $guideId) {
      ...fullAreaFragment
    }
  }
  ${fullAreaFragment}
`

export const CHECK_GUIDE_MEDIA = gql`
  mutation CheckGuideMedia($slug: String!) {
    checkGuideMedia(slug: $slug) {
      ...fullGuideFragment
    }
  }
  ${fullGuideFragment}
`

export const CREATE_BATCH_PROBLEMS = gql`
  mutation CreateBatchProblems(
    $referenceFeatureId: String
    $batchProblems: BatchProblemsInput!
  ) {
    createBatchProblems(
      referenceFeatureId: $referenceFeatureId
      batchProblems: $batchProblems
    ) {
      ...basicProblemFragment
      isDuplicate
      imageToBeUploaded
    }
  }
  ${basicProblemFragment}
`

export const CREATE_BATCH_ROUTES = gql`
  mutation CreateBatchRoutes(
    $referenceFeatureId: String
    $batchRoutes: BatchRoutesInput!
  ) {
    createBatchRoutes(
      referenceFeatureId: $referenceFeatureId
      batchRoutes: $batchRoutes
    ) {
      ...basicRouteFragment
      isDuplicate
      imageToBeUploaded
    }
  }
  ${basicRouteFragment}
`

export const CHECK_AREA_NAMES = gql`
  query CheckAreaNames($areaNames: AreaNamesInput!, $location: PointInput!) {
    checkAreaNames(areaNames: $areaNames, location: $location) {
      name
      isNew
    }
  }
`
